<template>
	<div>
		<div
			:ariaLabel="ariaLabel"
			:ariaLabelledby="ariaLabelledby"
			class="d-flex flex-column flex-sm-row align-start align-sm-center"
			role="tablist"
		>
			<p
				class="ma-0 mr-3 font-weight-semibold"
				:class="labelClasses"
			>
				{{ locales.label }}
			</p>

			<div class="d-flex">
				<StructureBtn
					v-for="(item, index) in items"
					:key="index"
					ref="structureBtns"
					:aria-selected="selected === index ? 'true' : 'false'"
					class="mr-3"
					:controls="`structure-panel-${index}`"
					role="tab"
					:selected="index === selected"
					:tabindex="selected === index ? 0 : -1"
					@click="onClick(index)"
					@keyup.left="pressLeft"
					@keyup.right="pressRight"
				>
					{{ item.label }}
				</StructureBtn>
			</div>
		</div>

		<div
			v-for="(tab, index) in items"
			:id="`structure-panel-${index}`"
			:key="index"
			role="tabpanel"
			:tabindex="selected === index ? 0 : -1"
		>
			<slot
				v-if="index === selected && searchBar"
				name="searchBar"
			/>

			<StructureList
				v-show="index === selected"
				v-model="structureValues[index]"
				:items="tab.items"
				:label="tab.listLabel || locales.listLabel"
				:max-items-loaded-default="maxItemsLoadedDefault"
				:name="`structures-${index}`"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { IStructureTabs, StructureTab } from './types';
	import { PropType, computed, nextTick, ref } from 'vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import StructureBtn from '../StructureBtn';
	import StructureList from '../StructureList';
	import { locales } from './locales';

	const props = defineProps({
		ariaLabel: {
			type: String,
			default: undefined,
		},
		ariaLabelledby: {
			type: String,
			default: undefined,
		},
		items: {
			type: Array as PropType<StructureTab[]>,
			default: () => [],
		},
		maxItemsLoadedDefault: {
			type: Number,
			default: 5,
		},
		searchBar: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Object as PropType<IStructureTabs>,
			default: undefined,
		},
	});

	const structureBtns = ref<typeof StructureBtn[]>();
	const breakpointHelper = new BreakpointHelper();
	const labelClasses = computed<string | undefined>(() => (breakpointHelper.is('xsOnly') ? 'mb-3' : undefined));
	const emit = defineEmits(['input']);

	const selected = computed({
		get() {
			return props.value.activeTab;
		},
		set(value: number) {
			emit('input', { ...props.value, activeTab: value });
		},
	});

	const structureValues = computed({
		get() {
			return props.value.activeValues;
		},
		set(value: Array<string | undefined>) {
			emit('input', { ...props.value, activeValues: value });
		},
	});

	const onClick = async(index: number): Promise<void> => {
		selected.value = index;
		await nextTick();
	};

	const pressLeft = async(): Promise<void> => {
		if (selected.value === 0) {
			selected.value = props.items.length - 1;
		} else {
			selected.value -= 1;
		}
		await nextTick();
		focusChange();
	};

	const pressRight = async(): Promise<void> => {
		if (selected.value === (props.items.length - 1)) {
			selected.value = 0;
		} else {
			selected.value += 1;
		}
		await nextTick();
		focusChange();
	};

	const focusChange = (): void => {
		if (structureBtns.value) {
			(structureBtns.value[selected.value].$el as HTMLElement).focus();
		}
	};
</script>

